import {Depots} from 'components/customer/Confirmation/Freight/Depots';
import React, {
    createContext,
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {Depot} from 'components/manufacturer/DepotSettings/depotApi';
import {
    DispatchMethod as Method,
    getDispatchMethod,
    useDispatchMethods,
} from 'hooks/Job/useDispatchMethods';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import {useGetDepotsQuery} from 'store/customer/depotApi';
import {DispatchMethods} from 'components/customer/Confirmation/Freight/DispatchMethods';
import {useAppContext} from 'contexts/ApplicationContext';
import {DepotFreightOptions} from 'store/customer/depotSlice';
import {JobForm} from 'components/customer/Job/entity/JobForm';
import {toFormDate} from 'components/customer/Job/helpers/toFormDate';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {
    jobDetailsSet,
    selectJobDetails,
} from 'components/customer/Confirmation/store/confirmationSlice';
import {DispatchMethod} from 'hooks';
import {useIsMobileLandscapeAndPortraitDevices} from 'shared/helpers/DeviceSize';

interface FreightContextType {
    isLoadingDepots?: boolean;
    selectedDepot?: Depot;
    setSelectedDepot?: Dispatch<SetStateAction<Depot | undefined>>;
    depots?: Depot[];
    formValues?: JobForm;
    setFormValues?: Dispatch<SetStateAction<JobForm>>;
    dispatchMethod?: number;
    setDispatchMethod?: Dispatch<SetStateAction<number | undefined>>;
    dispatchMethods?: Method[];
    requestedDeliveryDate?: string;
    setRequestedDeliveryDate?: Dispatch<SetStateAction<string | undefined>>;
}

const FreightContext = createContext<FreightContextType>({});

export const useFreightContext = () => useContext(FreightContext);

export const Freight = () => {
    const isMobileDevice = useIsMobileLandscapeAndPortraitDevices();
    const {userProfile} = useAppContext();
    const dispatch = useAppDispatch();
    const {job} = useJobContext() as PartialJob;

    const [selectedDepot, setSelectedDepot] = useState<Depot>();
    const [dispatchMethod, setDispatchMethod] = useState<number>();
    const [requestedDeliveryDate, setRequestedDeliveryDate] =
        useState<string>();
    const initialisedJobId = useRef<number>();

    useEffect(() => {
        // job can be undefined or empty on first render, so initialize the
        // dispatch method once the job has loaded. Only do this once per job
        // so a re-fetch does not override the customer's selection.
        if (!job?.id || initialisedJobId.current == job.id) return;

        initialisedJobId.current = job.id;
        setRequestedDeliveryDate(toFormDate(job.requestedDeliveryDate));
        setDispatchMethod(
            job.dispatchMethod ??
                getDispatchMethod(userProfile?.defaultFreightOption)
        );
    }, [job?.id, job?.dispatchMethod, userProfile?.defaultFreightOption]);

    const {dispatchMethods} = useDispatchMethods(selectedDepot);
    const {data: depotList, isLoading} = useGetDepotsQuery();

    const [initialValues, setInitialValues] = useState<JobForm>({});

    const jobDetails = useAppSelector(selectJobDetails);
    const jobDetailsRef = useRef(jobDetails);
    jobDetailsRef.current = jobDetails;

    useEffect(() => {
        if (job && Object.keys(job).length > 0) {
            const selected = jobDetailsRef.current;
            const keepSelected = !!selected?.street;

            setInitialValues({
                jobName: job.name,
                jobRefCode: job.endCustomerName,
                contactNumber: job.endContactNumber,
                description: job.description,
                requestedDeliveryDate: toFormDate(job?.requestedDeliveryDate),
                dispatch: dispatchMethod ?? job.dispatchMethod,
                depotId: selectedDepot ? selectedDepot.id : job.depotId,
                client: job.clientId,
                ...(keepSelected
                    ? {
                          street: selected.street,
                          state: selected.state,
                          suburb: selected.suburb,
                          postcode: selected.postcode,
                          city: selected.city ?? '',
                      }
                    : {
                          street: job.address,
                          state: job.addressState,
                          suburb: job.suburb,
                          postcode: job.postcode,
                          city: job.city ?? '',
                      }),
                save_address: false,
                set_default_address: false,
                files: [],
                attachments: [],
            });
        }
    }, [job, selectedDepot, dispatchMethod]);

    useEffect(() => {
        if (
            initialValues.dispatch == undefined ||
            initialValues.dispatch == DispatchMethod.PICKUP
        ) {
            dispatch(jobDetailsSet({...initialValues, requestedDeliveryDate}));
        }
    }, [initialValues, requestedDeliveryDate]);

    const depots = useMemo(() => {
        if (depotList?.setting.is_depot_functionality_enabled) {
            if (depotList.setting.selectable_by_customer) {
                return depotList.depots;
            } else {
                const defaultDepot = depotList.depots.find(
                    (depot) => depot.is_default
                );

                if (defaultDepot) {
                    return [defaultDepot];
                }
            }
        }

        return [];
    }, [depotList]);

    useEffect(() => {
        if (job && Object.keys(job).length > 0 && depots && depots.length) {
            const depot = depots.find((depot) => depot.id == job.depotId);

            if (depot) {
                setSelectedDepot(depot);
            } else {
                const defaultDepot = depots.find((depot) => depot.is_default);

                setSelectedDepot(defaultDepot);
            }
        }
    }, [job, depots]);

    useEffect(() => {
        if (selectedDepot) {
            if (
                selectedDepot.freight_pickup_option ==
                DepotFreightOptions.DELIVERY
            ) {
                // if depot only allows delivery and selected dispatch method is pickup change it to freight
                setDispatchMethod((method) =>
                    method == DispatchMethod.PICKUP
                        ? DispatchMethod.FREIGHT_CUSTOM_ADDRESS
                        : method
                );
            } else if (
                selectedDepot.freight_pickup_option ==
                DepotFreightOptions.PICKUP
            ) {
                // if depot only allows pickup and selected dispatch method is not pickup change it to pickup
                setDispatchMethod(DispatchMethod.PICKUP);
            }
        }
    }, [selectedDepot]);

    return (
        <FreightContext.Provider
            value={{
                selectedDepot,
                setSelectedDepot,
                isLoadingDepots: isLoading,
                depots,
                formValues: initialValues,
                setFormValues: setInitialValues,
                dispatchMethod,
                setDispatchMethod,
                dispatchMethods,
                requestedDeliveryDate,
                setRequestedDeliveryDate,
            }}>
            <Depots />

            {!userProfile?.depotSelectable ||
            !isMobileDevice ||
            depots.length <= 1 ? (
                <DispatchMethods />
            ) : null}
        </FreightContext.Provider>
    );
};
